const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type AnalyticsRequest = () => Promise<unknown>;

export interface AnalyticsRequestResult {
    status: 'fulfilled' | 'rejected';
    reason?: unknown;
}

export const getAnalyticsRequestConcurrency = (
    organizationCount: number,
    startDate: string,
    endDate: string
): number => {
    const startTime = Date.parse(startDate);
    const endTime = Date.parse(endDate);

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
        return 2;
    }

    const rangeDays = Math.max(1, Math.floor(Math.abs(endTime - startTime) / MILLISECONDS_PER_DAY) + 1);
    const organizationDays = Math.max(1, organizationCount) * rangeDays;

    if (organizationDays <= 31) {
        return 6;
    }

    if (organizationDays <= 155) {
        return 4;
    }

    if (organizationDays <= 620) {
        return 3;
    }

    return 2;
};

export const runAnalyticsRequests = async (
    requests: AnalyticsRequest[],
    concurrency: number,
    signal?: AbortSignal
): Promise<AnalyticsRequestResult[]> => {
    if (requests.length === 0) {
        return [];
    }

    const results = Array.from<unknown, AnalyticsRequestResult>({ length: requests.length }, () => ({
        status: 'rejected',
        reason: new Error('Analytics request was not started before the queue stopped')
    }));
    let nextRequestIndex = 0;

    const runWorker = async () => {
        while (!signal?.aborted) {
            const requestIndex = nextRequestIndex++;
            if (requestIndex >= requests.length) {
                return;
            }

            try {
                await requests[requestIndex]();
                results[requestIndex] = { status: 'fulfilled' };
            } catch (reason) {
                results[requestIndex] = { status: 'rejected', reason };
            }
        }
    };

    const normalizedConcurrency = Number.isFinite(concurrency) ? Math.floor(concurrency) : 1;
    const workerCount = Math.min(Math.max(1, normalizedConcurrency), requests.length);
    await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
    return results;
};
